'use client';

import React from 'react';
import { faWandMagicSparkles, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { PulseButton, Input } from 'src/components';
import useRouteEditorLogic from 'src/hooks/routes/useRouteEditorLogic';
import { StopList } from '../../../StopList';
import { DirectionsList } from '../../../DirectionsList';

/**
 * RouteEditor - Drawer-based route creation/editing component
 *
 * Pure render component — all business logic lives in useRouteEditorLogic.
 */
const RouteEditor: React.FC = () => {
  const {
    tempLabel,
    tempStops,
    directions,
    isLoadingDirections,
    isEditing,
    canSave,
    selectedRouteId,
    isLoading,
    isSaving,
    isDeleting,
    isOptimizing,
    canOptimize,
    optimizeError,
    handleLabelChange,
    handleReorder,
    handleRemove,
    handleSave,
    handleDelete,
    handleCancel,
    handleOptimize,
  } = useRouteEditorLogic();

  return (
    <div className="flex flex-col h-full" data-testid="route-editor">
      {/* Route Name Input */}
      <div className="px-4 py-2 border-b border-neutral-200 flex flex-col gap-2 flex-shrink-0">
        <Input
          value={tempLabel}
          onChange={handleLabelChange}
          placeholder="Enter route name..."
          variant="plain"
          size="lg"
          className="font-semibold text-lg"
          aria-label="Route name"
          data-testid="route-label-input"
        />

        {directions && (
          <div className="flex items-center gap-2 text-xs text-neutral-500">
            <span>&middot;</span>
            <span>{directions.totalDistanceText}</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="p-4 border-b border-neutral-200 flex items-center gap-2 shrink-0">
        <PulseButton
          variant="outline"
          color="neutral"
          size="sm"
          onClick={handleOptimize}
          disabled={!canOptimize}
          loading={isLoadingDirections || isOptimizing}
          aria-label="Optimize route order"
          data-testid="route-optimize-button"
          startDecorator={faWandMagicSparkles}
        >
          Optimize
        </PulseButton>
        {isEditing && selectedRouteId && (
          <PulseButton
            variant="outline"
            color="danger"
            size="sm"
            onClick={handleDelete}
            disabled={isOptimizing}
            loading={isDeleting}
            aria-label="Delete route"
            data-testid="route-delete-button"
            startDecorator={faTrash}
          >
            Delete
          </PulseButton>
        )}
      </div>

      {isEditing && selectedRouteId && canOptimize && (
        <p
          className="px-4 py-2 text-xs text-neutral-500 border-b border-neutral-200"
          data-testid="route-optimize-save-hint"
        >
          Optimizing saves your current stops first.
        </p>
      )}

      {optimizeError && (
        <p
          className="px-4 py-2 text-xs text-red-600 border-b border-neutral-200"
          role="alert"
          data-testid="route-optimize-error"
        >
          {optimizeError}
        </p>
      )}

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="p-4">
          <h3 className="text-sm font-semibold text-neutral-700 mb-3">
            Stops ({tempStops.filter((s) => !s.isUserLocation).length})
          </h3>
          {tempStops.length === 0 ? (
            <p className="text-sm text-neutral-500 italic">
              Click markers on the map or use the add button on results to add
              stops
            </p>
          ) : (
            <StopList
              stops={tempStops}
              onReorder={handleReorder}
              onRemove={handleRemove}
              isLoading={isLoading}
            />
          )}
        </div>

        {/* Directions */}
        {tempStops.length >= 2 && directions && (
          <div className="p-4 border-t border-neutral-200">
            <h3 className="text-sm font-semibold text-neutral-700 mb-3">
              Directions
            </h3>
            <DirectionsList directions={directions} />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-neutral-200 flex items-center justify-end gap-2 shrink-0">
        <PulseButton
          variant="outline"
          color="neutral"
          onClick={handleCancel}
          disabled={isSaving || isOptimizing}
          data-testid="route-cancel-button"
        >
          Cancel
        </PulseButton>
        <PulseButton
          variant="solid"
          color="primary"
          onClick={handleSave}
          loading={isSaving}
          disabled={!canSave || isSaving || isOptimizing}
          data-testid="route-save-button"
        >
          {isEditing ? 'Save Changes' : 'Create Route'}
        </PulseButton>
      </div>
    </div>
  );
};

export default RouteEditor;
